import { useCallback, useRef } from "react";
import type { TurnstileInstance } from "@marsidev/react-turnstile";

const DEFAULT_TIMEOUT_MS = 10_000;

export interface UseTurnstileToken {
  /** Attach to `<Turnstile ref={...}>`. */
  widgetRef: React.RefObject<TurnstileInstance | null>;
  /**
   * Request a fresh token. Resolves with the token delivered by the next
   * `onSuccess` (via `deliverToken`), or `undefined` when Turnstile is disabled.
   * Rejects after `timeoutMs` if none arrives. If a widget is already mounted it
   * is reset for a fresh token; otherwise `onMountNeeded` is invoked so the
   * caller can mount it (a freshly mounted widget auto-solves).
   */
  requestToken: () => Promise<string | undefined>;
  /**
   * Feed a token from the widget's `onSuccess` (or `""` from `onError`) to the
   * pending request. Returns true if a request was waiting; false if none — the
   * caller can then decide what to do with an unsolicited token (e.g. eager
   * bootstrap).
   */
  deliverToken: (token: string) => boolean;
}

/**
 * Shared Cloudflare Turnstile token plumbing for the public-Jan surfaces.
 * Owns the widget ref + the pending-resolver/timeout dance that PublicJanPage
 * (eager) and EmbeddedJanApp (lazy, shadow-DOM portal) both need; each consumer
 * keeps its own widget placement and success/error UX.
 */
export function useTurnstileToken(opts: {
  /** Whether a site key is configured (Turnstile enforced). */
  enabled: boolean;
  /** Mount the widget when a token is requested and none is mounted yet. */
  onMountNeeded?: () => void;
  timeoutMs?: number;
}): UseTurnstileToken {
  const { enabled, onMountNeeded, timeoutMs = DEFAULT_TIMEOUT_MS } = opts;
  const widgetRef = useRef<TurnstileInstance | null>(null);
  const pendingRef = useRef<((t: string) => void) | null>(null);
  const pendingPromiseRef = useRef<Promise<string | undefined> | null>(null);
  const onMountNeededRef = useRef(onMountNeeded);
  onMountNeededRef.current = onMountNeeded;

  const requestToken = useCallback((): Promise<string | undefined> => {
    if (!enabled) return Promise.resolve(undefined);
    // Dedupe concurrent requests: a single widget can only resolve one token at
    // a time, so share the in-flight promise rather than overwriting the
    // pending resolver (which would leave earlier callers hanging until timeout).
    if (pendingPromiseRef.current) return pendingPromiseRef.current;
    const promise = new Promise<string | undefined>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        if (pendingRef.current) {
          pendingRef.current = null;
          pendingPromiseRef.current = null;
          reject(new Error("turnstile_timeout"));
        }
      }, timeoutMs);
      pendingRef.current = (t) => {
        window.clearTimeout(timer);
        pendingPromiseRef.current = null;
        resolve(t);
      };
      if (widgetRef.current) widgetRef.current.reset();
      else onMountNeededRef.current?.();
    });
    pendingPromiseRef.current = promise;
    return promise;
  }, [enabled, timeoutMs]);

  const deliverToken = useCallback((token: string): boolean => {
    const pending = pendingRef.current;
    if (pending) {
      pendingRef.current = null;
      pending(token);
      return true;
    }
    return false;
  }, []);

  return { widgetRef, requestToken, deliverToken };
}
